/**
 * Screening billing catalog: two locked regions (Africa → NGN / Paystack,
 * everywhere else → USD / Stripe). Country picks the region; the client never
 * lets a company choose a foreign price list.
 */

export type BillingRegion = 'africa' | 'global';
export type PlanId = 'solo' | 'growth' | 'firm' | 'eagle';
export type BillingInterval = 'monthly' | 'annual';
export type BillingProvider = 'stripe' | 'paystack';
export type BillingStatus =
  | 'incomplete'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled';

export interface CountryOption {
  code: string;
  name: string;
}

export interface PlanCatalogEntry {
  id: PlanId;
  name: string;
  blurb: string;
  recruiterRange: string;
  monthlyAmount: number;
  annualAmount: number;
  voiceInterviews: number;
  applicationScores: number;
  voiceOverage: number;
  scoreOverage: number;
  extras: string[];
}

export interface RegionCatalog {
  region: BillingRegion;
  currency: 'USD' | 'NGN';
  provider: BillingProvider;
  currencySymbol: string;
  trialVoiceInterviews: number;
  quoteAboveInterviews: number;
  quoteFloorPerInterview: number;
  plans: PlanCatalogEntry[];
}

/** ISO 3166-1 alpha-2 codes billed on the Africa / NGN catalog. */
export const AFRICAN_COUNTRY_CODES: readonly string[] = [
  'DZ',
  'AO',
  'BJ',
  'BW',
  'BF',
  'BI',
  'CM',
  'CV',
  'CF',
  'TD',
  'KM',
  'CG',
  'CD',
  'CI',
  'DJ',
  'EG',
  'GQ',
  'ER',
  'SZ',
  'ET',
  'GA',
  'GM',
  'GH',
  'GN',
  'GW',
  'KE',
  'LS',
  'LR',
  'LY',
  'MG',
  'MW',
  'ML',
  'MR',
  'MU',
  'MA',
  'MZ',
  'NA',
  'NE',
  'NG',
  'RW',
  'ST',
  'SN',
  'SC',
  'SL',
  'SO',
  'ZA',
  'SS',
  'SD',
  'TZ',
  'TG',
  'TN',
  'UG',
  'ZM',
  'ZW',
];

const AFRICAN_SET = new Set(AFRICAN_COUNTRY_CODES);

/** Full country list for the signup select. US, UK, Canada, Nigeria first. */
export const COUNTRY_OPTIONS: CountryOption[] = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BR', name: 'Brazil' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CN', name: 'China' },
  { code: 'CI', name: "Côte d'Ivoire" },
  { code: 'DK', name: 'Denmark' },
  { code: 'EG', name: 'Egypt' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'IN', name: 'India' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'MX', name: 'Mexico' },
  { code: 'MA', name: 'Morocco' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NO', name: 'Norway' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'SN', name: 'Senegal' },
  { code: 'SG', name: 'Singapore' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'UG', name: 'Uganda' },
  { code: 'OTHER', name: 'Other' },
];

export const regionFromCountry = (countryCode: string): BillingRegion => {
  const code = (countryCode || '').trim().toUpperCase();
  if (!code || code === 'OTHER') return 'global';
  return AFRICAN_SET.has(code) ? 'africa' : 'global';
};

export const GLOBAL_CATALOG: RegionCatalog = {
  region: 'global',
  currency: 'USD',
  provider: 'stripe',
  currencySymbol: '$',
  trialVoiceInterviews: 25,
  quoteAboveInterviews: 600,
  quoteFloorPerInterview: 5,
  plans: [
    {
      id: 'solo',
      name: 'Starter',
      blurb: 'For a small talent team or a solo recruiter.',
      recruiterRange: '1–3 people on the desk',
      monthlyAmount: 349,
      annualAmount: 3490,
      voiceInterviews: 60,
      applicationScores: 180,
      voiceOverage: 12,
      scoreOverage: 0.5,
      extras: ['Unlimited roles and seats.', 'Permanent talent pool.'],
    },
    {
      id: 'growth',
      name: 'Growth',
      blurb: 'For a company hiring desk or an agency with a steady book.',
      recruiterRange: '4–15 people on the desk',
      monthlyAmount: 999,
      annualAmount: 9990,
      voiceInterviews: 200,
      applicationScores: 600,
      voiceOverage: 9,
      scoreOverage: 0.5,
      extras: ['Unlimited roles and seats.', 'Permanent talent pool.'],
    },
    {
      id: 'firm',
      name: 'Scale',
      blurb: 'For a larger talent function running volume across teams.',
      recruiterRange: '15+ people on the desk',
      monthlyAmount: 2499,
      annualAmount: 24990,
      voiceInterviews: 600,
      applicationScores: 1800,
      voiceOverage: 7,
      scoreOverage: 0.5,
      extras: [
        'Unlimited roles and seats.',
        'Permanent talent pool.',
        'Dedicated account manager.',
      ],
    },
  ],
};

export const AFRICA_CATALOG: RegionCatalog = {
  region: 'africa',
  currency: 'NGN',
  provider: 'paystack',
  currencySymbol: '₦',
  trialVoiceInterviews: 15,
  quoteAboveInterviews: 400,
  quoteFloorPerInterview: 3500,
  plans: [
    {
      id: 'solo',
      name: 'Starter',
      blurb: 'For a small talent team or a solo recruiter.',
      recruiterRange: '1–3 people on the desk',
      monthlyAmount: 150000,
      annualAmount: 1500000,
      voiceInterviews: 30,
      applicationScores: 90,
      voiceOverage: 7500,
      scoreOverage: 250,
      extras: ['Unlimited roles and seats.', 'Permanent talent pool.'],
    },
    {
      id: 'growth',
      name: 'Growth',
      blurb: 'For a company hiring desk or an agency with a steady book.',
      recruiterRange: '4–15 people on the desk',
      monthlyAmount: 450000,
      annualAmount: 4500000,
      voiceInterviews: 120,
      applicationScores: 360,
      voiceOverage: 6000,
      scoreOverage: 250,
      extras: ['Unlimited roles and seats.', 'Permanent talent pool.'],
    },
    {
      id: 'firm',
      name: 'Scale',
      blurb: 'For a larger talent function running volume across teams.',
      recruiterRange: '15+ people on the desk',
      monthlyAmount: 1200000,
      annualAmount: 12000000,
      voiceInterviews: 400,
      applicationScores: 1200,
      voiceOverage: 4500,
      scoreOverage: 250,
      extras: [
        'Unlimited roles and seats.',
        'Permanent talent pool.',
        'Dedicated account manager.',
      ],
    },
  ],
};

export const catalogForRegion = (region: BillingRegion): RegionCatalog =>
  region === 'africa' ? AFRICA_CATALOG : GLOBAL_CATALOG;

export const catalogForCountry = (countryCode: string): RegionCatalog =>
  catalogForRegion(regionFromCountry(countryCode));

export const processorCopy = (catalog: RegionCatalog): string =>
  catalog.region === 'africa'
    ? 'Billed in naira through Paystack'
    : 'Billed in US dollars through Stripe';

export const billingHintForCountry = (countryCode: string): string =>
  processorCopy(catalogForCountry(countryCode));

export const formatMoney = (
  amount: number,
  currency: 'USD' | 'NGN',
  options: { compact?: boolean } = {}
): string => {
  if (currency === 'NGN') {
    return `₦${amount.toLocaleString('en-NG')}`;
  }
  if (options.compact && amount % 1 !== 0) {
    return `$${amount.toFixed(2)}`;
  }
  return `$${amount.toLocaleString('en-US')}`;
};

export const planAmount = (
  plan: PlanCatalogEntry,
  interval: BillingInterval
): number => (interval === 'annual' ? plan.annualAmount : plan.monthlyAmount);

export const NEVER_PAY_FOR =
  'Roles, seats, the talent pool, shortlists, client presentations, and stored candidates stay inside the plan.';

export const trialCopy = (catalog: RegionCatalog): string =>
  `Your first live role is free. You get unlimited application scoring and ${catalog.trialVoiceInterviews} voice interviews on that role.`;

export const EAGLE_PLAN_ID: PlanId = 'eagle';

export const isEnterprisePlan = (planId: string | null | undefined): boolean =>
  planId === EAGLE_PLAN_ID;

export const isBillingReady = (
  status: BillingStatus | string | null | undefined,
  planId?: string | null
): boolean =>
  isEnterprisePlan(planId) || status === 'trialing' || status === 'active';
